import { useState, useRef } from 'react'
import { X, Link, Upload, ImagePlus, Video } from 'lucide-react'
import type { MediaItem } from '../../types'

interface Props {
  media: MediaItem[]
  canAddMore: boolean
  MAX_MEDIA: number
  MAX_IMG_MB: number
  MAX_VID_MB: number
  urlError: string
  setUrlError: (v: string) => void
  onFiles: (files: FileList | null) => void
  onRemove: (idx: number) => void
  onMove: (from: number, to: number) => void
  onAddFromUrl: (url: string) => string | null
  isUploading: boolean
}

export default function MediaUploader({
  media, canAddMore,
  MAX_MEDIA, MAX_IMG_MB, MAX_VID_MB,
  urlError, setUrlError,
  onFiles, onRemove, onMove, onAddFromUrl, isUploading
}: Props) {
  const [urlMode, setUrlMode]   = useState(false)
  const [urlInput, setUrlInput] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleUrlAdd = () => {
    const err = onAddFromUrl(urlInput)
    if (err) { setUrlError(err); return }
    setUrlInput(''); setUrlError(''); setUrlMode(false)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs font-semibold text-text-secondary mb-1">
            מדיה ({media.length}/{MAX_MEDIA})
          </p>
          <p className="text-[11px] text-text-muted -mt-0.5">
            תמונות עד {MAX_IMG_MB}MB · וידאו עד {MAX_VID_MB}MB
          </p>
        </div>
        {canAddMore && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setUrlMode(v => !v); setUrlError('') }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-medium border-none cursor-pointer transition-colors
                ${urlMode ? 'bg-[#D8F3DC] text-[#1B4332]' : 'bg-cream hover:bg-cream-dark text-text-secondary'}`}
            >
              <Link className="w-3.5 h-3.5" />
              URL
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-medium bg-cream hover:bg-cream-dark text-text-secondary border-none cursor-pointer transition-colors"
            >
              <Upload className="w-3.5 h-3.5" />
              העלה קובץ
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={e => onFiles(e.target.files)}
            />
          </div>
        )}
      </div>

      {/* URL input */}
      {urlMode && (
        <div className="flex gap-2 mb-3">
          <input
            value={urlInput}
            onChange={e => { setUrlInput(e.target.value); setUrlError('') }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleUrlAdd() } }}
            placeholder="https://example.com/image.jpg או video.mp4"
            dir="ltr"
            className={`flex-1 border-[1.5px] rounded-[10px] px-3 py-2 text-sm bg-cream outline-none transition-all focus:bg-white focus:shadow-[0_0_0_3px_rgba(45,106,79,0.10)]
              ${urlError ? 'border-[#E74C3C]' : 'border-cream-dark focus:border-green-primary'}`}
          />
          <button
            type="button"
            onClick={handleUrlAdd}
            className="px-4 py-2 rounded-[10px] bg-green-primary text-white text-sm font-semibold border-none cursor-pointer hover:brightness-105 transition-all"
          >
            הוסף
          </button>
        </div>
      )}
      {urlError && <p className="text-[11px] text-[#E74C3C] mb-2 font-medium">{urlError}</p>}

      {/* Empty state — drag & drop */}
      {media.length === 0 ? (
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer.files) }}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-[14px] py-10 flex flex-col items-center gap-2 cursor-pointer transition-colors
            ${dragOver ? 'border-green-primary bg-[#D8F3DC]/30' : 'border-cream-dark hover:border-green-primary hover:bg-cream/60'}`}
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-green-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-[12px] text-text-muted">מעלה...</p>
            </div>
          ) : (
            <>
              <ImagePlus className={`w-8 h-8 ${dragOver ? 'text-green-primary' : 'text-text-muted'}`} />
              <p className="text-[13px] font-medium text-text-secondary">גרור תמונה או וידאו לכאן</p>
              <p className="text-[11px] text-text-muted">תמונות עד {MAX_IMG_MB}MB · וידאו עד {MAX_VID_MB}MB</p>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-5 gap-2">
          
          {media.map((item, idx) => (
            <div
              key={item.src}
              className="relative group aspect-square rounded-[10px] overflow-hidden border-2 border-cream-dark bg-cream"
            >
              {item.type === 'video' ? (
                <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center relative">
                  <video
                    src={item.src}
                    className="w-full h-full object-cover"
                    muted
                    autoPlay
                    loop
                    playsInline
                    preload="metadata"
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
                      <Video className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              ) : (
                <img src={item.src} alt={`מדיה ${idx + 1}`} className="w-full h-full object-cover" />
              )}

              {idx === 0 && (
                <span className="absolute bottom-1 right-1 text-[9px] font-bold px-1.5 py-0.5 bg-black/60 text-white rounded-md leading-none">
                  ראשי
                </span>
              )}

              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                {idx > 0 && (
                  <button type="button" onClick={() => onMove(idx, idx - 1)} title="הזז שמאלה"
                    className="w-6 h-6 rounded-full bg-white/90 text-[#1A1A1A] text-[11px] font-bold border-none cursor-pointer flex items-center justify-center hover:bg-white">
                    →
                  </button>
                )}
                <button type="button" onClick={() => onRemove(idx)} title="הסר"
                  className="w-6 h-6 rounded-full bg-[#E74C3C] text-white border-none cursor-pointer flex items-center justify-center hover:bg-[#C0392B]">
                  <X className="w-3 h-3" />
                </button>
                {idx < media.length - 1 && (
                  <button type="button" onClick={() => onMove(idx, idx + 1)} title="הזז ימינה"
                    className="w-6 h-6 rounded-full bg-white/90 text-[#1A1A1A] text-[11px] font-bold border-none cursor-pointer flex items-center justify-center hover:bg-white">
                    ←
                  </button>
                )}
              </div>
            </div>
          ))}

          {isUploading && (
            <div className="aspect-square rounded-[10px] border-2 border-dashed border-green-primary bg-[#D8F3DC]/30 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-green-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {canAddMore && (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="aspect-square rounded-[10px] border-2 border-dashed border-cream-dark hover:border-green-primary hover:bg-cream/60 transition-colors cursor-pointer flex flex-col items-center justify-center gap-1"
            >
              <ImagePlus className="w-5 h-5 text-text-muted" />
              <span className="text-[10px] text-text-muted">הוסף</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}